import { useState, useEffect, useCallback } from 'react';
import { getRoles, getRoleLookup } from '../api/users';
import type { RoleDto, RoleLookupDto } from '../types';

interface UseRolesOptions {
  isActive?: boolean;
}

/**
 * Hook for fetching full roles list with details
 */
export function useRoles(options?: UseRolesOptions) {
  const [data, setData] = useState<RoleDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRoles = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const roles = await getRoles(options?.isActive);
      setData(roles);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to fetch roles');
    } finally {
      setIsLoading(false);
    }
  }, [options?.isActive]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchRoles,
  };
}

/**
 * Hook for fetching roles lookup for dropdowns
 */
export function useRoleLookup() {
  const [data, setData] = useState<RoleLookupDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRoleLookup = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const roles = await getRoleLookup();
      setData(roles);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to fetch roles');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoleLookup();
  }, [fetchRoleLookup]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchRoleLookup,
  };
}
